/**
 * Transaction Detail Page
 *
 * Full transaction detail view showing all fields for a single transaction.
 *
 * @fileoverview Transaction detail page at /transaction/:id
 *
 * @example
 * // In router
 * <Route path="/transaction/:id" element={<TransactionDetail />} />
 */

import React, { useEffect, useState } from 'react';
import { useParams, useNavigate, Link as RouterLink } from 'react-router-dom';
import {
  Box,
  VStack,
  HStack,
  Text,
  Badge,
  Spinner,
  Center,
  Button,
  Grid,
  GridItem,
  Heading,
  Divider,
  Link,
  useColorModeValue,
  Container,
  useToast,
} from '@chakra-ui/react';
import { MdArrowBack, MdOpenInNew, MdDownload } from 'react-icons/md';

import {
  AppShell,
  Sidebar,
  Header,
} from '../components/layout';
import { getNavItems } from '../components/layout/navItems';
import { ProfileMenu } from '../components/app';
import { useUser } from '../Context/userContext';
import { useAuth } from '../hooks/useAuth';
import { useSingleTransaction } from '../hooks/useTransactions';
import { formatAccountDisplay } from '../types/transactions';
import { supabase } from '../lib/supabase';
import { apiFetch } from '../lib/api';
import { buildApiError, toUserMessage } from '../lib/apiError';

/**
 * Logo component for header
 */
const Logo: React.FC = () => {
  const textColor = useColorModeValue('xacto.electricCyan', 'xacto.electricCyan');

  return (
    <HStack spacing={2}>
      <Box
        w="32px"
        h="32px"
        bg="xacto.electricCyan"
        borderRadius="md"
        display="flex"
        alignItems="center"
        justifyContent="center"
      >
        <Text color="xacto.deepNavy" fontWeight="bold" fontSize="lg">
          X
        </Text>
      </Box>
      <Text
        fontWeight="bold"
        fontSize="lg"
        color={textColor}
        display={{ base: 'none', md: 'block' }}
      >
        Xacto
      </Text>
    </HStack>
  );
};

/**
 * Loading state component
 */
const LoadingState: React.FC = () => (
  <Center h="100vh" bg="bg.primary">
    <VStack spacing={4}>
      <Spinner size="xl" color="accent.primary" thickness="4px" />
      <Text color="text.secondary">Loading...</Text>
    </VStack>
  </Center>
);

/**
 * Detail field component
 */
interface DetailFieldProps {
  label: string;
  value: string | number | boolean | null | undefined;
  isLink?: boolean;
  linkUrl?: string;
}

const DetailField: React.FC<DetailFieldProps> = ({ label, value, isLink, linkUrl }) => {
  const displayValue = value === null || value === undefined ? '-' : String(value);

  return (
    <Box overflow="hidden">
      <Text fontSize="xs" color="text.muted" textTransform="uppercase" mb={1} noOfLines={1}>
        {label}
      </Text>
      {isLink && linkUrl && value ? (
        <Link href={linkUrl} isExternal color="accent.primary" fontSize="sm" wordBreak="break-all">
          {displayValue} <MdOpenInNew style={{ display: 'inline' }} />
        </Link>
      ) : typeof value === 'boolean' ? (
        <Badge colorScheme={value ? 'green' : 'gray'} fontSize="sm">
          {value ? 'Yes' : 'No'}
        </Badge>
      ) : (
        <Text fontSize="sm" color="text.primary" wordBreak="break-word">
          {displayValue}
        </Text>
      )}
    </Box>
  );
};

/**
 * Section card component
 */
interface SectionCardProps {
  title: string;
  children: React.ReactNode;
}

const SectionCard: React.FC<SectionCardProps> = ({ title, children }) => (
  <Box
    bg="bg.surface"
    borderRadius="md"
    borderWidth="1px"
    borderColor="xacto.borderGray"
    p={4}
  >
    <Heading size="sm" color="accent.primary" mb={4}>
      {title}
    </Heading>
    {children}
  </Box>
);

/**
 * Format currency amount
 */
function formatAmount(amount: number, currency: string | null): string {
  const formatter = new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: currency || 'USD',
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });
  return formatter.format(Math.abs(amount));
}

/**
 * Format datetime
 */
function formatDateTime(dateStr: string | null | undefined): string {
  if (!dateStr) return '-';
  const date = new Date(dateStr);
  return date.toLocaleString('en-US', {
    weekday: 'long',
    year: 'numeric',
    month: 'long',
    day: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
    second: '2-digit',
    timeZoneName: 'short',
  });
}

/**
 * Format date only
 */
function formatDate(dateStr: string | null | undefined): string {
  if (!dateStr) return '-';
  const date = new Date(dateStr);
  return date.toLocaleDateString('en-US', {
    weekday: 'long',
    year: 'numeric',
    month: 'long',
    day: 'numeric',
  });
}

/**
 * Transaction detail content component
 */
const TransactionDetailContent: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { user, selectedTenant, isLoading: userLoading } = useUser();
  const toast = useToast();

  const [isDownloadingPdf, setIsDownloadingPdf] = useState(false);

  const { transaction, isLoading, error } = useSingleTransaction({
    transactionId: id, // id from URL is the database UUID, passed as transactionId for backwards compatibility
  });

  // Get nav items based on user role
  const isAdmin = user?.globalRole === 'ADMIN';
  const isTenantAdmin = isAdmin || selectedTenant?.isOwner || selectedTenant?.tenantRole === 'TENANT_ADMIN';
  const navItems = getNavItems(isAdmin, isTenantAdmin);

  /**
   * Map technical error messages to user-friendly messages
   */
  const getErrorMessage = (errorMsg: string): string => {
    if (errorMsg.includes('Not a member of this tenant')) {
      return "You don't have access to this transaction. It may belong to a different workspace.";
    }
    if (errorMsg.includes('Transaction not found')) {
      return 'This transaction could not be found. It may have been deleted.';
    }
    if (errorMsg.includes('Unauthorized')) {
      return 'You need to be logged in to view this transaction.';
    }
    if (errorMsg.includes('Forbidden')) {
      return 'You do not have permission to view this transaction.';
    }
    return 'Unable to load the transaction. Please try again.';
  };

  /**
   * Handle sidebar navigation
   */
  const handleNavigate = (path: string): void => {
    navigate(path);
  };

  /**
   * Handle PDF download - calls backend API
   * Server-side generation provides:
   * - Real text in PDF (not images) = selectable, searchable
   * - Automatic pagination for multi-page documents
   * - Crystal clear fonts and rendering
   * - No performance impact on client
   */
  const handleDownloadPdf = async (): Promise<void> => {
    if (!transaction) {
      toast({
        title: "We couldn't generate the PDF",
        description: 'The transaction is still loading. Please try again in a moment.',
        status: 'error',
        // Milliseconds, not seconds — at `5` the toast vanished before it
        // could be read.
        duration: 5000,
        isClosable: true,
      });
      return;
    }

    setIsDownloadingPdf(true);
    try {
      // Get auth session for API call
      const { data: { session } } = await supabase.auth.getSession();
      if (!session?.access_token) {
        throw new Error('Your session has expired. Please sign in again.');
      }

      // Call backend PDF endpoint
      // Call backend PDF endpoint using apiFetch to handle URL construction correctly
      // This ensures we hit the backend (port 8000) and not the frontend (port 3000)
      const response = await apiFetch(
        `/api/pdf/transaction/${transaction.id}`,
        {
          method: 'GET',
          headers: {
            'x-tenant-id': selectedTenant?.id || '',
          },
        },
        session.access_token
      );

      if (!response.ok) {
        throw buildApiError(response.status, await response.text());
      }

      // Get PDF blob
      const pdfBlob = await response.blob();
      if (pdfBlob.size === 0) {
        throw new Error(
          "We couldn't generate that PDF. Please try again in a moment."
        );
      }

      // Create download link
      const url = window.URL.createObjectURL(pdfBlob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `transaction-${transaction.id}.pdf`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);

      toast({
        title: 'Success',
        description: 'Transaction PDF downloaded successfully.',
        status: 'success',
        duration: 3000,
        isClosable: true,
      });
    } catch (err) {
      console.error('PDF download error:', err);
      toast({
        title: "We couldn't download the PDF",
        description: toUserMessage(err, 'Please try again in a moment.'),
        status: 'error',
        duration: 5000,
        isClosable: true,
      });
    } finally {
      setIsDownloadingPdf(false);
    }
  };

  // Show loading while data loads
  if (userLoading || isLoading) {
    return <LoadingState />;
  }

  // Error state
  if (error) {
    return (
      <AppShell
        header={
          <Header
            logo={<Logo />}
            title="Transaction Detail"
            userMenu={<ProfileMenu />}
          />
        }
        sidebar={
          <Sidebar
            items={navItems}
            currentPath=""
            onNavigate={handleNavigate}
          />
        }
      >
        <VStack spacing={4} align="center" pt={8}>
          <Text color="red.400" fontSize="lg">
            {getErrorMessage(error)}
          </Text>
          <Button
            onClick={() => navigate(-1)}
            leftIcon={<MdArrowBack />}
            variant="outline"
            color="accent.primary"
            borderColor="xacto.electricCyan"
            _hover={{ bg: 'xacto.mutedNavy' }}
          >
            Back
          </Button>
        </VStack>
      </AppShell>
    );
  }

  // No transaction found
  if (!transaction) {
    return (
      <AppShell
        header={
          <Header
            logo={<Logo />}
            title="Transaction Detail"
            userMenu={<ProfileMenu />}
          />
        }
        sidebar={
          <Sidebar
            items={navItems}
            currentPath=""
            onNavigate={handleNavigate}
          />
        }
      >
        <VStack spacing={4} align="center" pt={8}>
          <Text color="text.muted" fontSize="lg">
            Transaction not found
          </Text>
          <Button
            onClick={() => navigate(-1)}
            leftIcon={<MdArrowBack />}
            variant="outline"
            color="accent.primary"
            borderColor="xacto.electricCyan"
            _hover={{ bg: 'xacto.mutedNavy' }}
          >
            Back
          </Button>
        </VStack>
      </AppShell>
    );
  }

  // Determine amount color
  const amountColor = transaction.amount < 0 ? 'green.400' : 'text.primary';
  const amountLabel = transaction.amount < 0 ? 'Credit' : 'Debit';

  return (
    <AppShell
      header={
        <Header
          logo={<Logo />}
          title="Transaction Detail"
          userMenu={<ProfileMenu />}
        />
      }
      sidebar={
        <Sidebar
          items={navItems}
          currentPath=""
          onNavigate={handleNavigate}
        />
      }
    >
      <Container maxW="container.2xl" py={4} px={6} centerContent>
        <VStack spacing={6} align="stretch" w="full">
          {/* Back navigation and PDF download */}
          <HStack justify="space-between" w="full">
            <Button
              onClick={() => navigate(-1)}
              leftIcon={<MdArrowBack />}
              variant="ghost"
              color="text.secondary"
              size="sm"
              _hover={{ bg: 'xacto.mutedNavy' }}
            >
              Back
            </Button>
            <Button
              onClick={handleDownloadPdf}
              isLoading={isDownloadingPdf}
              loadingText="Generating..."
              leftIcon={<MdDownload />}
              variant="outline"
              color="xacto.electricCyan"
              borderColor="xacto.electricCyan"
              size="sm"
              _hover={{ bg: 'xacto.mutedNavy' }}
            >
              Download PDF
            </Button>
          </HStack>

          {/* Transaction detail content */}
          <VStack spacing={6} align="stretch" w="full">
            {/* Page header */}
            <Box>
              <HStack spacing={4} mb={2}>
                <Heading size="lg" color="text.primary">
                  Transaction Detail
                </Heading>
                {transaction.pending && (
                  <Badge colorScheme="yellow" fontSize="sm">
                    Pending
                  </Badge>
                )}
              </HStack>
              <Text fontSize="sm" color="text.muted" fontFamily="mono" wordBreak="break-all">
                {transaction.transactionId}
              </Text>
            </Box>

            {/* Amount highlight */}
            <Box
              bg="bg.surface"
              borderRadius="md"
              borderWidth="1px"
              borderColor="xacto.borderGray"
              p={6}
              textAlign="center"
            >
              <Text fontSize="xs" color="text.muted" mb={2}>
                {amountLabel}
              </Text>
              <Text fontSize="3xl" fontWeight="bold" color={amountColor}>
                {transaction.amount < 0 ? '-' : ''}
                {formatAmount(transaction.amount, transaction.isoCurrencyCode)}
              </Text>
              <Text fontSize="sm" color="text.muted" mt={1}>
                {transaction.isoCurrencyCode || 'USD'}
              </Text>
            </Box>

            {/* Detail sections */}
            <Grid templateColumns={{ base: '1fr', lg: 'repeat(2, 1fr)' }} gap={6}>
              {/* Section 1: Core Details */}
              <GridItem>
                <SectionCard title="Core Details">
                  <Grid templateColumns={{ base: '1fr', sm: 'repeat(2, 1fr)' }} gap={4}>
                    <DetailField label="Transaction ID" value={transaction.transactionId} />
                    <DetailField label="Pending Transaction ID" value={transaction.pendingTransactionId} />
                    <DetailField label="Date" value={formatDate(transaction.date)} />
                    <DetailField label="Date/Time" value={formatDateTime(transaction.datetime)} />
                    <DetailField label="Authorized Date" value={formatDate(transaction.authorizedDate)} />
                    <DetailField label="Authorized Date/Time" value={formatDateTime(transaction.authorizedDatetime)} />
                    <DetailField label="Amount" value={formatAmount(transaction.amount, transaction.isoCurrencyCode)} />
                    <DetailField label="Currency" value={transaction.isoCurrencyCode} />
                    <DetailField label="Pending" value={transaction.pending} />
                    <DetailField label="Name" value={transaction.name} />
                  </Grid>
                </SectionCard>
              </GridItem>

              {/* Section 2: Merchant Information */}
              <GridItem>
                <SectionCard title="Merchant Information">
                  <Grid templateColumns={{ base: '1fr', sm: 'repeat(2, 1fr)' }} gap={4}>
                    <DetailField label="Merchant Name" value={transaction.merchantName} />
                    <DetailField label="Merchant Entity ID" value={transaction.merchantEntityId} />
                    <DetailField
                      label="Logo URL"
                      value={transaction.logoUrl ? 'View Logo' : null}
                      isLink={!!transaction.logoUrl}
                      linkUrl={transaction.logoUrl || undefined}
                    />
                    <DetailField
                      label="Website"
                      value={transaction.website}
                      isLink={!!transaction.website}
                      linkUrl={transaction.website || undefined}
                    />
                  </Grid>
                </SectionCard>
              </GridItem>

              {/* Section 3: Account Information */}
              <GridItem>
                <SectionCard title="Account Information">
                  <Grid templateColumns={{ base: '1fr', sm: 'repeat(2, 1fr)' }} gap={4}>
                    <DetailField label="Institution" value={transaction.plaidAccount?.plaidItem?.institutionName} />
                    <DetailField label="Account" value={formatAccountDisplay(transaction.plaidAccount)} />
                    <DetailField label="Account Name" value={transaction.plaidAccount?.name} />
                    <DetailField label="Official Name" value={transaction.plaidAccount?.officialName} />
                    <DetailField label="Account Type" value={transaction.plaidAccount?.type} />
                    <DetailField label="Account Subtype" value={transaction.plaidAccount?.subtype} />
                    <DetailField label="Account Mask" value={transaction.plaidAccount?.mask ? `****${transaction.plaidAccount.mask}` : null} />
                    <DetailField label="Plaid Account ID" value={transaction.plaidAccountId} />
                    <GridItem colSpan={{ base: 1, sm: 2 }}>
                      <DetailField label="Plaid Item ID" value={transaction.plaidItemId} />
                    </GridItem>
                  </Grid>
                </SectionCard>
              </GridItem>

              {/* Section 4: Categorization */}
              <GridItem>
                <SectionCard title="Categorization">
                  <Grid templateColumns={{ base: '1fr', sm: 'repeat(2, 1fr)' }} gap={4}>
                    <DetailField label="PFC Primary" value={transaction.personalFinanceCategoryPrimary} />
                    <DetailField label="PFC Detailed" value={transaction.personalFinanceCategoryDetailed} />
                    <DetailField label="Confidence Level" value={transaction.personalFinanceCategoryConfidenceLevel} />
                    <DetailField
                      label="PFC Icon"
                      value={transaction.personalFinanceCategoryIconUrl ? 'View Icon' : null}
                      isLink={!!transaction.personalFinanceCategoryIconUrl}
                      linkUrl={transaction.personalFinanceCategoryIconUrl || undefined}
                    />
                    <DetailField label="Category ID" value={transaction.categoryId} />
                    <GridItem colSpan={{ base: 1, sm: 2 }}>
                      <DetailField
                        label="Categories"
                        value={transaction.category?.length ? transaction.category.join(' > ') : null}
                      />
                    </GridItem>
                  </Grid>
                </SectionCard>
              </GridItem>

              {/* Section 5: Location */}
              <GridItem>
                <SectionCard title="Location">
                  <Grid templateColumns={{ base: '1fr', sm: 'repeat(2, 1fr)' }} gap={4}>
                    <GridItem colSpan={{ base: 1, sm: 2 }}>
                      <DetailField label="Address" value={transaction.locationAddress} />
                    </GridItem>
                    <DetailField label="City" value={transaction.locationCity} />
                    <DetailField label="Region" value={transaction.locationRegion} />
                    <DetailField label="Postal Code" value={transaction.locationPostalCode} />
                    <DetailField label="Country" value={transaction.locationCountry} />
                    <DetailField label="Latitude" value={transaction.locationLat} />
                    <DetailField label="Longitude" value={transaction.locationLon} />
                    <DetailField label="Store Number" value={transaction.locationStoreNumber} />
                  </Grid>
                </SectionCard>
              </GridItem>

              {/* Section 6: Payment Details */}
              <GridItem>
                <SectionCard title="Payment Details">
                  <Grid templateColumns={{ base: '1fr', sm: 'repeat(2, 1fr)' }} gap={4}>
                    <DetailField label="Payment Channel" value={transaction.paymentChannel} />
                    <DetailField label="Transaction Type" value={transaction.transactionType} />
                    <DetailField label="Transaction Code" value={transaction.transactionCode} />
                    <DetailField label="Classification" value={transaction.transactionClass} />
                    <DetailField label="Check Number" value={transaction.checkNumber} />
                    <DetailField label="Original Description" value={transaction.originalDescription} />
                  </Grid>
                </SectionCard>
              </GridItem>

              {/* Section 7: Metadata */}
              <GridItem colSpan={{ base: 1, lg: 2 }}>
                <SectionCard title="Metadata">
                  <Grid templateColumns={{ base: '1fr', md: 'repeat(2, 1fr)', xl: 'repeat(4, 1fr)' }} gap={4}>
                    <DetailField label="Created At" value={formatDateTime(transaction.createdAt)} />
                    <DetailField label="Updated At" value={formatDateTime(transaction.updatedAt)} />
                    <DetailField label="Retrieved At" value={formatDateTime(transaction.retrievedAt)} />
                    <DetailField label="Sync Cursor" value={transaction.syncCursor} />
                    <DetailField label="Is Removed" value={transaction.isRemoved} />
                    <DetailField label="Removed At" value={formatDateTime(transaction.removedAt)} />
                    <DetailField label="Tenant ID" value={transaction.tenantId} />
                    <DetailField label="Internal ID" value={transaction.id} />
                  </Grid>
                </SectionCard>
              </GridItem>
            </Grid>
          </VStack>
        </VStack>
      </Container>
    </AppShell>
  );
};

/**
 * Transaction Detail Page
 *
 * Shows all fields for a single transaction including:
 * - Core transaction data
 * - Merchant information
 * - Account information
 * - Categorization
 * - Location
 * - Payment details
 * - Metadata
 *
 * Requires authentication - redirects to home if not authenticated.
 *
 * @returns Transaction detail page
 */
export const TransactionDetail: React.FC = () => {
  const navigate = useNavigate();
  const { user, session, loading: authLoading } = useAuth();

  // Redirect to login if not authenticated
  useEffect(() => {
    if (!authLoading && (!user || !session)) {
      navigate('/');
    }
  }, [authLoading, user, session, navigate]);

  // Show loading while checking auth
  if (authLoading) {
    return <LoadingState />;
  }

  // Don't render if not authenticated
  if (!user || !session) {
    return null;
  }

  // TransactionDetailContent uses UserProvider from AppRouter context
  return <TransactionDetailContent />;
};

export default TransactionDetail;
